import { memo, useEffect, useState } from 'react'
import { formatRFC3339DateTime } from '~/shared/lib/date'
import { parseISO } from 'date-fns'
import type { FC } from 'react'
import { useSelector } from 'react-redux'
import { useFormik } from 'formik'
import {
  Button,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  TextField,
  type TextFieldProps,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { WorklistGridRow } from './WorkUnitTable'
import { patientsSlice } from '~/entities/person'
import type { PatientUser } from '~/shared/types'
import { useQueryClient } from '@tanstack/react-query'
import { FFModal, Loader } from '~/shared/ui'
import { useGroupOptions, UserGroupSelect } from '~/entities/assignee-group'
import FormLabel from '@mui/material/FormLabel'
import { PatientDetailTopBar, usePatientDetail } from '~/features/member-profile'
import type { CaseAction } from '~/entities/work-unit'
import Autocomplete from '@mui/material/Autocomplete'
import { createWorklistEditorFields } from './utils'
import { listWorkListKey, useUpdateWorkUnitItem } from './WorkListsService'
import { getItemIsClosed } from './utils'
import { useAppDispatch } from '~/app/store/hooks'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { asDisplayText } from '~/shared/lib/displayText'
import { required } from '~/shared/lib/required'
import { logger } from '~/shared/lib/logger'

/**
 * What this editor's form holds: the grid row, plus the `action` the operator picks from the
 * status dropdown. `action` is a form field written by formik, not something a row carries.
 */
type WorklistEditorValues = WorklistGridRow & { action?: string }

interface WorklistEditorProps {
  worklistRow: WorklistEditorValues
  handleClose: () => void
}

const WorklistEditor: FC<WorklistEditorProps> = props => {
  const [loading, setLoading] = useState(false)
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const { worklistRow } = props
  const { switchPatient } = usePatientDetail()
  const patient = useSelector(state => {
    return worklistRow.patientId ? state.patients.byId[Number(worklistRow.patientId)] : null
  })
  const [modalData, setModalData] = useState<{
    values: WorklistEditorValues
    action: string
    descriptions: string[]
  } | null>(null)
  // Setting the current status and action as the selected action. To show it as the selected action in the dropdown.
  const [selectedAction, setSelectedAction] = useState<CaseAction>({
    dest: worklistRow.status || '',
    trigger: worklistRow.action,
  })
  const { mutateAsync: updateWorkUnitItem } = useUpdateWorkUnitItem()

  useEffect(() => {
    const patientId = worklistRow.patientId
    if (patientId != null) {
      fireAndForget('[Worklist] refetch patient', () =>
        dispatch(patientsSlice.thunks.getPatient(patientId)),
      )
      switchPatient(patientId)
    }
  }, [worklistRow.patientId, dispatch, switchPatient])

  const providers = useSelector(state => state.providers)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: worklistRow,
    onSubmit: values => {
      const descriptions = selectedAction.descriptions ?? []
      if (descriptions.length > 0) {
        setModalData({
          values: values,
          action: required(values.action, 'the selected action'),
          descriptions: descriptions,
        })
      } else {
        void updateWorklistRow(values)
      }
    },
  })

  const closeEditor = () => {
    setModalData(null)
    props.handleClose()
  }

  const updateWorklistRow = async (values: WorklistEditorValues) => {
    setLoading(true)
    try {
      // Save through the mutation hook rather than the generated client directly:
      // the hook invalidates the work-unit-items query backing the grid rows, so
      // the row behind this drawer re-renders with the saved value.
      const { data: updatedWorkUnitItem } = await updateWorkUnitItem({
        content_type: required(values.contentType, 'a content type on the worklist row'),
        object_id: Number(values.id),
        due_date: values.dueDate ? formatRFC3339DateTime(parseISO(values.dueDate)) : undefined,
        // `ownerGroup` arrives as a string from the row builder and as a number
        // from the owner picker; `null` clears the owner and `undefined` must
        // stay omitted so an untouched owner is left alone.
        owner_group: values.ownerGroup == null ? values.ownerGroup : Number(values.ownerGroup),
        action: values.action,
      })

      // Item counts live on the worklist itself, so a case that just left the
      // list needs the worklist query refreshed too.
      if (
        updatedWorkUnitItem.content_object.content_type === 'case' &&
        getItemIsClosed(updatedWorkUnitItem.content_object)
      ) {
        await queryClient.invalidateQueries({ queryKey: [listWorkListKey] })
      }

      formik.resetForm({ values: values })
      closeEditor()
    } catch (error) {
      // The mutation hook surfaces the failure as a toast. Leave the drawer open
      // with the user's edits intact so they can retry.
      logger.error('[Worklist] update worklist row', error)
    } finally {
      setLoading(false)
    }
  }

  const localWorklistRow: WorklistGridRow = formik.values
  const setValues = (name: string, value: TextFieldProps['value']) => ({
    name,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      e.persist()
      formik.handleChange(e)
      void formik.setFieldTouched(name, true, false)
    },
    value,
  })

  const readOnlyFields = createWorklistEditorFields()

  const { selectedCaseOwnerGroupOption, flatOptions, groupedOptions } = useGroupOptions(
    parseInt(formik.values.ownerGroup || '') || null,
    false,
    patient,
    patient?.person?.care_team,
    false,
  )
  return (
    <Box sx={{ p: '16px', height: '100%' }}>
      {modalData == null ? null : (
        <div>
          <FFModal open={true}>
            <>
              <Box sx={{ m: 2 }}>
                <h1>
                  <b>Review the action(s) that will be automated with this status change.</b>
                </h1>
                <List>
                  {modalData.descriptions.map(description => {
                    return (
                      <ListItem key={description}>
                        <ListItemIcon>
                          <CheckIcon />
                        </ListItemIcon>
                        <ListItemText>
                          <b>{description}</b>
                        </ListItemText>
                      </ListItem>
                    )
                  })}
                </List>
                <Box sx={{ m: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button onClick={() => setModalData(null)} disabled={loading}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => void updateWorklistRow(modalData.values)}
                    disabled={loading}
                    color="primary"
                    variant="contained"
                  >
                    OK
                  </Button>
                </Box>
              </Box>
            </>
          </FFModal>
        </div>
      )}
      <Box
        component="form"
        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        onSubmit={formik.handleSubmit}
      >
        {patient?.onboarding_state &&
        patient?.person &&
        Object.keys(patient.onboarding_state).length > 0 ? (
          <div>
            <PatientDetailTopBar patient={patient as PatientUser} providers={providers} />
          </div>
        ) : null}
        <Box sx={{ p: '24px' }}>
          <Grid container spacing={1} direction="row" sx={{ flexGrow: 1 }}>
            {readOnlyFields.map(field => (
              <Grid key={field.id ?? field.fieldName} size={6}>
                <TextField
                  name={field.displayName}
                  value={
                    asDisplayText(localWorklistRow[field.fieldName]) ||
                    (field.id ? asDisplayText(localWorklistRow[field.id]) : null) ||
                    '-'
                  }
                  label={field.displayName}
                  sx={{
                    mt: '16px',
                    color: 'common.black',
                    '& label.MuiInputLabel-root': { color: 'common.black' },
                  }}
                  disabled={true}
                  fullWidth
                  multiline
                  key={field.id}
                  slotProps={{
                    input: {
                      disableUnderline: true,
                    },
                  }}
                ></TextField>
              </Grid>
            ))}
          </Grid>

          {localWorklistRow.dueDate ? (
            <TextField
              {...setValues('dueDate', localWorklistRow.dueDate)}
              type="date"
              label="Next action Date"
              sx={{ mt: '16px' }}
              disabled={loading}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          ) : null}
          <Box sx={{ mt: 1 }}>
            <FormLabel>Status</FormLabel>
            <Autocomplete
              style={{ width: '50%' }}
              disableClearable={true}
              autoHighlight
              value={selectedAction}
              options={localWorklistRow.actions ?? []}
              getOptionLabel={option =>
                option.trigger && option.trigger !== option.dest
                  ? `${option.trigger.toUpperCase()} (${option.dest.toUpperCase()})`
                  : option.dest.toUpperCase()
              }
              onChange={(event, newValue) => {
                if (newValue) {
                  setSelectedAction(newValue)
                }

                void formik.setFieldValue('action', newValue?.trigger)
                void formik.setFieldTouched('action', true, false)
              }}
              renderInput={params => <TextField {...params} variant="standard" />}
            />
          </Box>

          <Box sx={{ mt: 1 }}>
            <FormLabel>Owner</FormLabel>
            {
              <UserGroupSelect
                selectedValue={selectedCaseOwnerGroupOption ?? null}
                options={flatOptions}
                groupedOptions={groupedOptions}
                onChange={ownerGroupOption => {
                  const ownerGroup = ownerGroupOption.value
                  void formik.setFieldValue('ownerGroup', ownerGroup)
                  void formik.setFieldTouched('ownerGroup', true, false)
                }}
                disabled={loading}
                patient={patient?.person ? (patient as PatientUser) : null}
              />
            }
          </Box>
        </Box>
        <Box
          sx={{
            mt: '16px',
            display: 'flex',
            flex: 1,
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
          }}
        >
          {loading && <Loader inline />}
          <Button type="button" disabled={loading} onClick={closeEditor}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={loading || !formik.dirty}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default memo(WorklistEditor)
